import React, { useMemo } from "react";

import clsx from "clsx";
import { twMerge } from "tailwind-merge";

import { formatting } from "@north-beam/nb-common";

const AT_PACE_THRESHOLD = 0.03;
const OVER = "OVER" as const;
const UNDER = "UNDER" as const;
const AT_PACE = "AT_PACE" as const;

export const getIsOnPace = (actual: number, budgeted: number) =>
  Math.abs(actual - budgeted) / budgeted < AT_PACE_THRESHOLD ||
  actual === budgeted;

export const getPacingStatus = (actual: number, budgeted: number) => {
  if (getIsOnPace(actual, budgeted)) {
    return AT_PACE;
  }
  return actual > budgeted ? OVER : UNDER;
};

const getPacingIcon = (status: typeof OVER | typeof UNDER | typeof AT_PACE) => {
  switch (status) {
    case AT_PACE:
      return "check";
    case OVER:
      return "arrow-up";
    case UNDER:
      return "arrow-down";
    default:
      return "dash";
  }
};

interface Props {
  actual: number;
  budgeted: number;
  className?: string;
  subject?: string;
  prefix?: string;
  isNegativeTrendGood?: boolean;
  style?: React.CSSProperties;
  concise?: boolean;
}

export const PacingStatus = ({
  actual,
  budgeted,
  className,
  subject = "budget",
  prefix = "",
  isNegativeTrendGood,
  style,
  concise = false,
}: Props) => {
  const overUnderBudget = actual - budgeted;
  const pacingStatus = getPacingStatus(actual, budgeted);
  const overUnderBudgetPercent = overUnderBudget / budgeted;
  const overUnderBudgetFormatted = formatting.formatPercentage(
    Math.abs(overUnderBudgetPercent),
    { precision: "approximate" },
  );
  const pacingText =
    pacingStatus === AT_PACE
      ? concise
        ? "On target"
        : "Pacing on target"
      : concise
      ? `${overUnderBudgetFormatted}`
      : `${prefix} ${overUnderBudgetFormatted} ${pacingStatus.toLowerCase()} ${subject}`;

  /*
    isNegativeTrendGood has 3 states: true, false, undefined
    true: the over is bad, the under is good
    false: the over is good, the under is bad
    undefined: the over is info, the under is info
  */
  const status = useMemo(() => {
    if (pacingStatus === AT_PACE) {
      return "good";
    }

    if (isNegativeTrendGood === true) {
      if (pacingStatus === OVER) {
        return "bad";
      } else if (pacingStatus === UNDER) {
        return "good";
      }
    } else if (isNegativeTrendGood === false) {
      if (pacingStatus === OVER) {
        return "good";
      } else if (pacingStatus === UNDER) {
        return "bad";
      }
    }

    if (pacingStatus === OVER) {
      return "info";
    } else if (pacingStatus === UNDER) {
      return "info";
    }
    return "info";
  }, [isNegativeTrendGood, pacingStatus]);

  return (
    <div
      className={twMerge(
        clsx(
          "rounded-md px-2 py-0.5 w-fit flex items-center whitespace-nowrap",
          {
            "bg-mars-2 text-mars": status === "bad",
            "bg-cosmic-3 text-cosmic": status === "info",
            "bg-alien-2 text-alien": status === "good",
          },
        ),
        className,
      )}
      style={style}
    >
      <div className="flex items-center">
        {!prefix && (
          <i
            className={clsx(
              "fa-regular inline-block mr-1",
              `fa-${getPacingIcon(pacingStatus)}`,
            )}
          />
        )}
        {pacingText}
      </div>
    </div>
  );
};
